/**
 * ColorThemeProvider — global 10-color-theme system.
 *
 * Complements the existing light/dark `ThemeProvider` (which toggles the
 * `.dark` class). This provider sets `data-color-theme="<name>"` on
 * `document.documentElement`, which the per-theme CSS blocks in `index.css`
 * key off (both light `[data-color-theme="X"]` and dark
 * `.dark[data-color-theme="X"]`).
 *
 * Persistence:
 *   - localStorage (`sgrc-color-theme`) immediately, for instant + offline.
 *   - settings-service (per-user, cross-device) via
 *     `PUT /api/v1/settings/user/preferences/appearance { preferences: { colorTheme } }`.
 *     On load we read the backend value and reconcile (backend wins if present).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import apiClient from '@/lib/api/client'
import {
  DEFAULT_THEME,
  isThemeName,
  THEME_STORAGE_KEY,
  type ThemeName,
} from '@/lib/themes'

interface ColorThemeContextValue {
  colorTheme: ThemeName
  setColorTheme: (theme: ThemeName) => void
}

const ColorThemeContext = createContext<ColorThemeContextValue | undefined>(undefined)

const APPEARANCE_ENDPOINT = '/settings/user/preferences/appearance'

function readStoredTheme(): ThemeName {
  if (typeof window === 'undefined') return DEFAULT_THEME
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (isThemeName(stored)) return stored
  } catch {
    /* localStorage unavailable */
  }
  return DEFAULT_THEME
}

function applyColorTheme(theme: ThemeName) {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-color-theme', theme)
}

interface ColorThemeProviderProps {
  children: ReactNode
}

export function ColorThemeProvider({ children }: ColorThemeProviderProps) {
  const [colorTheme, setColorThemeState] = useState<ThemeName>(() => readStoredTheme())
  const didReconcile = useRef(false)

  // Apply attribute immediately on mount + on every change.
  useEffect(() => {
    applyColorTheme(colorTheme)
  }, [colorTheme])

  // Reconcile with backend once (backend wins if it has a stored value).
  useEffect(() => {
    if (didReconcile.current) return
    didReconcile.current = true
    // Skip the authed reconcile on UNAUTHENTICATED public routes (e.g. the
    // "/p/:slug" storefront). Firing an authed GET there 401s → keycloak
    // updateToken fails → a bare console.error, tripping the zero-error gate.
    const p = typeof window !== 'undefined' ? window.location.pathname : ''
    const isPublic = ['/p', '/signup', '/landing'].some((pp) => p === pp || p.startsWith(pp + '/'))
    if (isPublic) return
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await apiClient.get(APPEARANCE_ENDPOINT)
        const prefs = (data?.preferences ?? data ?? {}) as Record<string, unknown>
        const remote = prefs.colorTheme ?? prefs.color_theme
        if (!cancelled && isThemeName(remote)) {
          setColorThemeState(remote)
          try {
            window.localStorage.setItem(THEME_STORAGE_KEY, remote)
          } catch {
            /* ignore */
          }
        }
      } catch {
        // No appearance endpoint / not authenticated yet — localStorage value stands.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const setColorTheme = useCallback((next: ThemeName) => {
    setColorThemeState(next)
    applyColorTheme(next)
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
    // Fire-and-forget cross-device persist; failure is non-fatal (localStorage already set).
    apiClient
      .put(APPEARANCE_ENDPOINT, { preferences: { colorTheme: next } })
      .catch(() => {
        /* endpoint may not exist; localStorage-only fallback is acceptable */
      })
  }, [])

  return (
    <ColorThemeContext.Provider value={{ colorTheme, setColorTheme }}>
      {children}
    </ColorThemeContext.Provider>
  )
}

export function useColorTheme(): ColorThemeContextValue {
  const ctx = useContext(ColorThemeContext)
  if (!ctx) {
    throw new Error('useColorTheme must be used within a ColorThemeProvider')
  }
  return ctx
}
